import {useMemo, useState} from "react";
import "./ThesisComparison.css";
import type {SimulationResult} from "@signum/api-contracts";

const colors = ["#8762c6", "#d6773c", "#368bb5", "#b95083", "#829332"];
const signed = (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
export function ThesisResultsSelector({result, selected, onSelect}: {
  result: SimulationResult; selected: string; onSelect(id: string): void;
}) {
  if (!result.theses) return null;
  const views: Array<{id: string; name: string; result: SimulationResult}> =
    [{id: "", name: "Combined stock pool", result}, ...result.theses];
  return <section className="panel thesis-results">
    <div className="panel-title"><div><span className="eyebrow">Independent decisions · pooled performance</span><h2>Combined & individual results</h2></div></div>
    <p>Each recommendation is a separate lot. A stock recommended by two theses is bought twice.
      Formation and exit rules stay independent; return percentages are not added.</p>
    <div className="thesis-result-options" role="group" aria-label="Select result to inspect">{views.map((view) =>
      <button type="button" key={view.id} className={selected === view.id ? "selected" : ""}
        aria-pressed={selected === view.id} onClick={() => onSelect(view.id)}>
        <strong>{view.name}</strong><b>{view.result.metrics.totalReturnPct === undefined
          ? "Not activated" : signed(view.result.metrics.totalReturnPct)}</b>
        <small>{view.result.metrics.openTrades ?? 0} open lots · {view.result.metrics.closedTrades} closed lots</small>
        <small>Active from {view.result.combination?.activationDate
          ?? view.result.performanceFormation?.activationDate ?? "not yet"}</small>
      </button>)}</div>
    {!selected && <p className="summary-foot">Combined starts when the first thesis activates.
      Each later thesis joins from the next session after its activation close, at its existing lot values.
      Its formation-period gains are not backdated into Combined. Holdings include formation lots.</p>}
  </section>;
}

export function MultiThesisChart({result}: {result: SimulationResult}) {
  const [hidden, setHidden] = useState<string[]>([]);
  const [hover, setHover] = useState<number>();
  const points = result.series;
  const lines = useMemo(() => [
    {id: "combined", name: "Combined", color: "#176c5b", values: points.map((p) => p.simulationIndex)},
    ...(result.theses ?? []).map((thesis, i) => ({id: `thesis:${thesis.id}`, name: thesis.name, color: colors[i % colors.length]!,
      values: thesis.result.series.map((p) => p.performanceActive === false ? undefined : p.simulationIndex)})),
    {id: "spy", name: "SPY", color: "#778ba3", values: points.map((p) => p.performanceActive ? p.benchmarkIndex : undefined)},
    {id: "qqq", name: "QQQ", color: "#c0a36b", values: points.map((p) => p.performanceActive ? p.qqqIndex : undefined)},
  ], [result]);
  const visible = lines.filter((line) => !hidden.includes(line.id));
  const width = 1000; const height = 330; const left = 62; const right = 16; const pad = 22;
  const values = visible.flatMap((line) => line.values.filter((v): v is number => v !== undefined));
  let min = 100; let max = 100;
  for (const value of values) {min = Math.min(min, value); max = Math.max(max, value);}
  const span = Math.max(max - min, 1);
  const x = (index: number) => left + index / Math.max(points.length - 1, 1) * (width - left - right);
  const y = (value: number) => pad + (max - value) / span * (height - pad * 2);
  const path = (series: Array<number | undefined>, include = (_index: number) => true) => {
    let started = false;
    return series.map((value, index) => {
      if (value === undefined || !include(index)) {started = false; return "";}
      const command = started ? "L" : "M"; started = true;
      return `${command}${x(index)},${y(value)}`;
    }).join(" ");
  };
  return <section className="panel thesis-comparison">
    <div className="panel-title"><div><span className="eyebrow">Performance comparison</span><h2>All theses & combined pool</h2></div></div>
    <div className="thesis-chart-legend">{lines.map((line) => <label key={line.id}>
      <input type="checkbox" checked={!hidden.includes(line.id)} onChange={() => setHidden(
        hidden.includes(line.id) ? hidden.filter((id) => id !== line.id) : [...hidden, line.id])}/>
      <i style={{background: line.color}}/>{line.name}
    </label>)}</div>
    <div className="thesis-chart-canvas">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img"
        aria-label="Combined, individual thesis, SPY and QQQ performance"
        onPointerMove={(event) => {const bounds = event.currentTarget.getBoundingClientRect();
          const cursor = (event.clientX - bounds.left) / bounds.width * width;
          setHover(Math.max(0, Math.min(points.length - 1,
            Math.round((cursor - left) / (width - left - right) * (points.length - 1)))));
        }} onPointerLeave={() => setHover(undefined)}>
        {[0,.25,.5,.75,1].map((part) => <g key={part}>
          <line x1={left} x2={width-right} y1={pad+part*(height-pad*2)} y2={pad+part*(height-pad*2)} stroke="#e3e8e8"/>
          <text x={left-8} y={pad+part*(height-pad*2)+4} textAnchor="end" fontSize="12" fill="#6b767a">{(max-part*span-100).toFixed(0)}%</text>
        </g>)}
        {visible.map((line) => <g key={line.id}>
          {line.id === "combined" && <path d={path(line.values, (i) => !points[i]?.performanceActive
            || !points[i-1]?.performanceActive)} stroke="#b8c0c4" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke"/>}
          <path d={path(line.values, (i) => line.id !== "combined" || points[i]?.performanceActive !== false)}
            stroke={line.color} strokeWidth={line.id === "combined" ? 3 : 1.6} fill="none" vectorEffect="non-scaling-stroke"/>
        </g>)}
        {hover !== undefined && points[hover] && <line x1={x(hover)} x2={x(hover)} y1={pad} y2={height-pad} stroke="#889196" strokeDasharray="4 4"/>}
      </svg>
      {hover !== undefined && points[hover] && <div className="thesis-chart-tooltip"
        style={{left: `${Math.min(73, x(hover) / width * 100)}%`}}>
        <strong>{points[hover]!.date}</strong>
        {visible.map((line) => <span key={line.id}><i style={{background: line.color}}/>{line.name}<b>
          {line.values[hover] === undefined ? "Not active" : signed(line.values[hover]! - 100)}</b></span>)}
      </div>}
    </div>
    <div className="thesis-chart-dates"><span>{points[0]?.date}</span><span>{points.at(-1)?.date}</span></div>
    <p className="summary-foot">Gray: Combined before activation. Each thesis starts at its own activation baseline.
      SPY and QQQ use the Combined activation date. Select a thesis above for its own period-matched comparisons.</p>
  </section>;
}
